import { AuditoriaAccion } from '@enum/auditoria/auditoria-keys.enum';
import { SignoAuditoriaEvento } from '@interfaces/interface-bakend/auditoria/signo-auditoria-evento.interface';

export interface InscripcionAccionMeta {
    label: string;
    icono: string;
}

export const INSCRIPCION_ACCIONES: Record<string, InscripcionAccionMeta> = {
    [AuditoriaAccion.INSCRIPCION_INICIO]: { label: 'Inicio', icono: 'play_circle' },
    [AuditoriaAccion.REGISTRO_OK]: { label: 'Usuario registrado', icono: 'person_add' },
    [AuditoriaAccion.REGISTRO_ERROR]: { label: 'Error de registro', icono: 'person_off' },
    [AuditoriaAccion.RECUPERACION_CUENTA]: { label: 'Cuenta reutilizada', icono: 'restore' },
    [AuditoriaAccion.SOLICITUD_CREADA]: { label: 'Solicitud creada', icono: 'note_add' },
    [AuditoriaAccion.SOLICITUD_ERROR]: { label: 'Error de solicitud', icono: 'error_outline' },
    [AuditoriaAccion.UPLOAD_OK]: { label: 'Documento subido', icono: 'cloud_done' },
    [AuditoriaAccion.UPLOAD_ERROR]: { label: 'Error de subida', icono: 'cloud_off' },
    [AuditoriaAccion.COMPRESION_OK]: { label: 'Imagen procesada', icono: 'image' },
    [AuditoriaAccion.INSCRIPCION_ERROR]: { label: 'Error de inscripción', icono: 'report_problem' },
    [AuditoriaAccion.GLOBAL_ERROR]: { label: 'Error global', icono: 'bug_report' },
    [AuditoriaAccion.TIMEOUT]: { label: 'Timeout', icono: 'timer_off' },
    [AuditoriaAccion.DDJJ_OK]: { label: 'DDJJ enviada', icono: 'fact_check' },
    [AuditoriaAccion.DDJJ_ERROR]: { label: 'Error de DDJJ', icono: 'report' },
    [AuditoriaAccion.INSCRIPCION_FIN]: { label: 'Inscripción completada', icono: 'check_circle' },
};

export function labelAccionInscripcion(accion?: string): string {
    return (accion && INSCRIPCION_ACCIONES[accion]?.label) || accion || 'Acción desconocida';
}

export function iconoAccionInscripcion(accion?: string): string {
    return (accion && INSCRIPCION_ACCIONES[accion]?.icono) || 'help_outline';
}

export function emailDeEvento(evento: SignoAuditoriaEvento): string {
    return evento.usuario?.email || (evento.payload?.email as string) || '-';
}

export function nombreDeEvento(evento: SignoAuditoriaEvento): string {
    return evento.usuario?.nombreCompleto || '';
}

/** Referencia de solicitud (FK) asociada al evento. */
export function solicitudDeEvento(evento: SignoAuditoriaEvento): string {
    return evento.solicitudId || (evento.payload?.solicitudId as string) || '';
}

/** Resumen corto y legible de un evento de inscripción. */
export function resumenEvento(evento: SignoAuditoriaEvento): string {
    const p = evento.payload || {};
    const partes: string[] = [];
    if (p.paso) { partes.push(`${p.paso}`); }
    if (p.label) { partes.push(p.label); }
    if (p.bytes) { partes.push(`${Math.round(Number(p.bytes) / 1024)} KB`); }
    if (typeof p.duracionMs === 'number') { partes.push(`${p.duracionMs} ms`); }
    if (p.error) { partes.push(`⚠ ${String(p.error).slice(0, 120)}`); }
    return partes.join(' · ');
}

/** Un intento de inscripción agrupado por traceId (o _id si faltara). */
export interface GrupoInscripcion {
    clave: string;
    traceId: string;
    email: string;
    nombre: string;
    solicitudId: string;
    eventos: SignoAuditoriaEvento[];
    totalPasos: number;
    ultimo: SignoAuditoriaEvento;
    resultado: string;
    fecha: string;
}

/**
 * Agrupa los eventos sueltos en "un objeto por intento" (traceId). El
 * resultado es una lista legible: un renglón por intento de inscripción.
 */
export function agruparPorIntento(
    eventos: SignoAuditoriaEvento[]
): GrupoInscripcion[] {
    const mapa = new Map<string, SignoAuditoriaEvento[]>();

    eventos.forEach((evento) => {
        const clave = evento.traceId || evento._id;
        const lista = mapa.get(clave) || [];
        lista.push(evento);
        mapa.set(clave, lista);
    });

    const grupos: GrupoInscripcion[] = [];

    mapa.forEach((eventosDelGrupo, clave) => {
        const ordenados = eventosDelGrupo
            .slice()
            .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
        const ultimo = ordenados[ordenados.length - 1];
        const conEmail = ordenados.find((e) => emailDeEvento(e) !== '-');
        const conNombre = ordenados.find((e) => !!nombreDeEvento(e));
        const conSolicitud = ordenados.find((e) => !!solicitudDeEvento(e));
        const hayError = ordenados.some((e) => e.resultado === 'ERROR');

        grupos.push({
            clave,
            traceId: ultimo.traceId || '',
            email: conEmail ? emailDeEvento(conEmail) : '-',
            nombre: conNombre ? nombreDeEvento(conNombre) : '',
            solicitudId: conSolicitud ? solicitudDeEvento(conSolicitud) : '',
            eventos: ordenados,
            totalPasos: ordenados.length,
            ultimo,
            resultado: hayError ? 'ERROR' : ultimo.resultado || 'EXITO',
            fecha: ultimo.fecha,
        });
    });

    return grupos.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
}
